import { Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';
import { DiagnosticFormData } from '../../types/diagnostic';
import { calculateSecurityScore, getRecommendations, getVulnerableAreas } from '../../utils/securityCalculator';

Font.register({
  family: 'Roboto',
  fonts: [
    { src: '/fonts/Roboto-Regular.ttf', fontWeight: 400 },
    { src: '/fonts/Roboto-Bold.ttf',    fontWeight: 700 },
  ],
});

// ── Palette ───────────────────────────────────────────────────────────────────

const C = {
  blue:        '#3b82f6',
  red:         '#ef4444',
  orange:      '#f97316',
  green:       '#22c55e',
  purple:      '#8b5cf6',
  text:        '#111827',
  textMuted:   '#6b7280',
  textLight:   '#9ca3af',
  bg:          '#ffffff',
  cardBg:      '#f9fafb',
  cardBorder:  '#e5e7eb',
  headerBg:    '#111827',
  scoreBg:     '#1e3a5f',
  blueLight:   '#93c5fd',
  dark:        '#374151',
  darkLight:   '#4b5563',
};

// ── Color helpers ─────────────────────────────────────────────────────────────

function scoreColor(s: number): string {
  if (s < 35) return C.red;
  if (s < 65) return C.orange;
  return C.green;
}
function scoreLevelLabel(s: number): string {
  if (s < 35) return 'NIVEAU FAIBLE';
  if (s < 65) return 'NIVEAU MOYEN';
  return 'BON NIVEAU';
}
function priorityColor(p: string): string {
  if (p === 'high')   return C.red;
  if (p === 'medium') return C.orange;
  return C.textMuted;
}
function priorityLabel(p: string): string {
  if (p === 'high')   return 'HAUTE';
  if (p === 'medium') return 'MOYENNE';
  return 'BASSE';
}
function riskColor(r: string): string {
  if (r === 'high')   return C.red;
  if (r === 'medium') return C.orange;
  return C.blue;
}
function riskBg(r: string): string {
  if (r === 'high')   return '#fef2f2';
  if (r === 'medium') return '#fff7ed';
  return '#eff6ff';
}
function riskLabel(r: string): string {
  if (r === 'high')   return 'ÉLEVÉ';
  if (r === 'medium') return 'MODÉRÉ';
  return 'FAIBLE';
}

// ── Interfaces ────────────────────────────────────────────────────────────────

interface ImprovementAction {
  id: string;
  action_title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  estimated_cost: number;
  score_impact: number;
  status: string;
}

interface PDFDocumentProps {
  formData: DiagnosticFormData;
  improvementActions?: ImprovementAction[];
  companyName?: string;
  companyLogoUrl?: string;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = StyleSheet.create({
  // Page
  page: {
    fontFamily: 'Roboto',
    backgroundColor: C.bg,
    paddingTop: 0,
    paddingBottom: 30,
    paddingLeft: 0,
    paddingRight: 0,
  },

  // ── Page 1 header ──────────────────────────────────────────────────────────
  header: {
    backgroundColor: C.headerBg,
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 20,
    paddingRight: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  headerLogo: { width: 48, height: 48, objectFit: 'contain' },
  headerText: { flexDirection: 'column' },
  headerCompany: { fontSize: 16, fontWeight: 'bold', color: '#ffffff', marginBottom: 3 },
  headerSubtitle: { fontSize: 9, color: C.blueLight },
  headerRight: { alignItems: 'flex-end' },
  headerConfidential: { fontSize: 8, color: C.textLight, marginBottom: 4 },
  headerDate: { fontSize: 9, color: '#ffffff', marginBottom: 4 },
  headerCity: { fontSize: 10, fontWeight: 'bold', color: '#ffffff' },

  // ── Score banner ───────────────────────────────────────────────────────────
  scoreBanner: {
    backgroundColor: C.scoreBg,
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 20,
    paddingRight: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  scoreBannerLabel: { fontSize: 10, color: C.blueLight },
  scoreRow: { flexDirection: 'row', alignItems: 'flex-end' },
  scoreNumber: { fontSize: 48, fontWeight: 'bold', lineHeight: 1 },
  scoreSlash: { fontSize: 20, color: C.blueLight, marginBottom: 6, marginLeft: 4 },
  levelBadge: { paddingTop: 4, paddingBottom: 4, paddingLeft: 10, paddingRight: 10, borderRadius: 4 },
  levelBadgeText: { fontSize: 9, fontWeight: 'bold', color: '#ffffff' },

  // ── Category cards ─────────────────────────────────────────────────────────
  categoryRow: {
    flexDirection: 'row',
    paddingTop: 16,
    paddingBottom: 0,
    paddingLeft: 20,
    paddingRight: 20,
    gap: 12,
  },
  categoryCard: {
    flex: 1,
    backgroundColor: C.cardBg,
    borderWidth: 1,
    borderColor: C.cardBorder,
    borderStyle: 'solid',
    borderRadius: 4,
    overflow: 'hidden',
  },
  categoryBand: { height: 4 },
  categoryBody: { padding: 10 },
  categoryTitle: { fontSize: 9, fontWeight: 'bold', color: C.dark, marginBottom: 6 },
  categoryScoreRow: { flexDirection: 'row', alignItems: 'flex-end', marginBottom: 6 },
  categoryScore: { fontSize: 24, fontWeight: 'bold' },
  categoryScoreMax: { fontSize: 12, color: C.textMuted, marginBottom: 3, marginLeft: 2 },
  progressBg: {
    height: 4,
    backgroundColor: C.cardBorder,
    borderRadius: 2,
    marginBottom: 4,
  },
  progressFill: { height: 4, borderRadius: 2 },
  categoryLevel: { fontSize: 8, color: C.textMuted },

  // ── Generic section ────────────────────────────────────────────────────────
  section: {
    paddingTop: 16,
    paddingBottom: 0,
    paddingLeft: 20,
    paddingRight: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: C.text,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: C.cardBorder,
    borderBottomStyle: 'solid',
    marginBottom: 10,
  },

  // ── Vulnerability cards ────────────────────────────────────────────────────
  vulnCard: {
    marginBottom: 8,
    borderRadius: 4,
    overflow: 'hidden',
    borderLeftWidth: 3,
    borderLeftStyle: 'solid',
  },
  vulnBody: { paddingTop: 8, paddingBottom: 8, paddingLeft: 10, paddingRight: 10 },
  vulnHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 4, gap: 6 },
  vulnBadge: { paddingTop: 2, paddingBottom: 2, paddingLeft: 6, paddingRight: 6, borderRadius: 3 },
  vulnBadgeText: { fontSize: 7, fontWeight: 'bold', color: '#ffffff' },
  vulnTitle: { fontSize: 10, fontWeight: 'bold', color: C.text },
  vulnDesc: { fontSize: 9, color: C.dark, marginBottom: 4 },

  // ── Recommendation rows ────────────────────────────────────────────────────
  recRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 6,
    paddingBottom: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: C.cardBorder,
    borderBottomStyle: 'solid',
    gap: 8,
  },
  recBadge: {
    paddingTop: 3,
    paddingBottom: 3,
    paddingLeft: 6,
    paddingRight: 6,
    borderRadius: 3,
    minWidth: 52,
    alignItems: 'center',
  },
  recBadgeText: { fontSize: 7, fontWeight: 'bold', color: '#ffffff' },
  recContent: { flex: 1 },
  recTitle: { fontSize: 9, fontWeight: 'bold', color: C.text },
  recDesc: { fontSize: 8, color: C.textMuted },
  recGain: { fontSize: 9, fontWeight: 'bold', color: C.green, minWidth: 40, textAlign: 'right' },
  recCost: { fontSize: 9, color: C.text, minWidth: 50, textAlign: 'right' },

  // ── Footer ─────────────────────────────────────────────────────────────────
  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 20,
    paddingRight: 20,
    borderTopWidth: 1,
    borderTopColor: C.cardBorder,
    borderTopStyle: 'solid',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: C.bg,
  },
  footerText: { fontSize: 7, color: C.textLight },

  // ── Page 2 — simplified header ─────────────────────────────────────────────
  headerSmall: {
    backgroundColor: C.headerBg,
    height: 30,
    paddingLeft: 20,
    paddingRight: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerSmallLeft: { fontSize: 10, fontWeight: 'bold', color: '#ffffff' },
  headerSmallRight: { fontSize: 10, color: C.blueLight },

  // ── Summary banner ─────────────────────────────────────────────────────────
  summaryBanner: {
    marginTop: 16,
    marginLeft: 20,
    marginRight: 20,
    backgroundColor: '#f0f9ff',
    borderWidth: 1,
    borderColor: '#bae6fd',
    borderStyle: 'solid',
    borderRadius: 4,
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 10,
    paddingRight: 10,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  summaryMetric: { alignItems: 'center' },
  summaryMetricValue: { fontSize: 16, fontWeight: 'bold', color: '#0369a1', textAlign: 'center' },
  summaryMetricLabel: { fontSize: 8, color: '#0369a1', textAlign: 'center', marginTop: 2 },
  summaryDivider: { width: 1, height: 30, backgroundColor: '#bae6fd' },

  // ── Actions table ──────────────────────────────────────────────────────────
  tableContainer: { marginTop: 12, marginLeft: 20, marginRight: 20 },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    borderBottomWidth: 1,
    borderBottomColor: C.cardBorder,
    borderBottomStyle: 'solid',
  },
  tableHeaderText: { fontSize: 8, fontWeight: 'bold', color: C.dark },
  tableRow: {
    flexDirection: 'row',
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: C.cardBorder,
    borderBottomStyle: 'solid',
    backgroundColor: C.bg,
  },
  tableRowAlt: {
    flexDirection: 'row',
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: C.cardBorder,
    borderBottomStyle: 'solid',
    backgroundColor: C.cardBg,
  },
  colAction:   { width: '55%' },
  colPriority: { width: '15%', alignItems: 'center' },
  colGain:     { width: '15%', alignItems: 'center' },
  colCost:     { width: '15%', alignItems: 'flex-end' },
  actionTitle: { fontSize: 9, fontWeight: 'bold', color: C.text, marginBottom: 2 },
  actionDesc:  { fontSize: 7, color: C.textMuted },
  tableBadge:  { paddingTop: 2, paddingBottom: 2, paddingLeft: 5, paddingRight: 5, borderRadius: 3 },
  tableBadgeText: { fontSize: 7, fontWeight: 'bold', color: '#ffffff' },
  tableGain: { fontSize: 9, fontWeight: 'bold', color: C.green },
  tableCost: { fontSize: 9, color: C.text },
});

// ── Component ─────────────────────────────────────────────────────────────────

export default function PDFDocument({
  formData,
  improvementActions = [],
  companyName,
  companyLogoUrl,
}: PDFDocumentProps) {
  const brandName  = companyName || 'OPTISECUR';
  const year       = new Date().getFullYear();
  const today      = new Date().toLocaleDateString('fr-FR');

  let securityData = {
    score: 0,
    details: [] as { label: string; score: number; maxScore: number; percentage: number }[],
    categoryScores: {} as Record<string, number>,
  };
  let recommendations: { title: string; priority: string; items: string[] }[] = [];
  let vulnerableAreas: { label: string; risk: string; description: string }[] = [];

  try { securityData    = calculateSecurityScore(formData); } catch {}
  try { recommendations = getRecommendations(formData);     } catch {}
  try { vulnerableAreas = getVulnerableAreas(formData);     } catch {}

  const { score, details } = securityData;

  const activeActions     = improvementActions.filter(a => a.status !== 'cancelled');
  const hasPage2          = activeActions.length > 0;
  const totalPages        = hasPage2 ? 2 : 1;
  const highPriorityCount = activeActions.filter(a => a.priority === 'high').length;
  const totalCost         = activeActions.reduce((sum, a) => sum + Number(a.estimated_cost), 0);

  // 3 category cards
  const categories = [
    { label: 'Protection physique', color: C.blue,   score: details[0]?.score ?? 0 },
    { label: 'Surveillance',        color: C.purple, score: details[1]?.score ?? 0 },
    { label: 'Dissuasion',          color: C.orange, score: details[2]?.score ?? 0 },
  ];

  // Page 1 recommendations: improvementActions (top 6) if present, else category recs
  const page1Actions  = activeActions.slice(0, 6);
  const useActionRecs = activeActions.length > 0;

  const cityLine = [formData.city, formData.postalCode].filter(Boolean).join(' ');

  // ── Footer helper ────────────────────────────────────────────────────────
  const Footer = ({ page }: { page: number }) => (
    <View style={S.footer}>
      <Text style={S.footerText}>© {year} {brandName}</Text>
      <Text style={S.footerText}>Page {page}/{totalPages}</Text>
      <Text style={S.footerText}>Document confidentiel — usage personnel</Text>
    </View>
  );

  return (
    <Document>

      {/* ════════════════════════════════ PAGE 1 ════════════════════════════════ */}
      <Page size="A4" style={S.page}>

        {/* 1 ── Header */}
        <View style={S.header}>
          <View style={S.headerLeft}>
            {companyLogoUrl && (
              <Image src={companyLogoUrl} style={S.headerLogo} />
            )}
            <View style={S.headerText}>
              <Text style={S.headerCompany}>{brandName}</Text>
              <Text style={S.headerSubtitle}>Diagnostic de Performance Sécuritaire</Text>
            </View>
          </View>
          <View style={S.headerRight}>
            <Text style={S.headerConfidential}>Rapport confidentiel</Text>
            <Text style={S.headerDate}>{today}</Text>
            {cityLine ? <Text style={S.headerCity}>{cityLine}</Text> : null}
          </View>
        </View>

        {/* 2 ── Score banner */}
        <View style={S.scoreBanner}>
          <Text style={S.scoreBannerLabel}>Score de sécurité global</Text>
          <View style={S.scoreRow}>
            <Text style={[S.scoreNumber, { color: scoreColor(score) }]}>{score}</Text>
            <Text style={S.scoreSlash}>/100</Text>
          </View>
          <View style={[S.levelBadge, { backgroundColor: scoreColor(score) }]}>
            <Text style={S.levelBadgeText}>{scoreLevelLabel(score)}</Text>
          </View>
        </View>

        {/* 3 ── Category cards */}
        <View style={S.categoryRow}>
          {categories.map((cat) => (
            <View key={cat.label} style={S.categoryCard}>
              <View style={[S.categoryBand, { backgroundColor: cat.color }]} />
              <View style={S.categoryBody}>
                <Text style={S.categoryTitle}>{cat.label}</Text>
                <View style={S.categoryScoreRow}>
                  <Text style={[S.categoryScore, { color: cat.color }]}>{cat.score}</Text>
                  <Text style={S.categoryScoreMax}>/100</Text>
                </View>
                <View style={S.progressBg}>
                  <View
                    style={[
                      S.progressFill,
                      { width: `${cat.score}%`, backgroundColor: cat.color },
                    ]}
                  />
                </View>
                <Text style={S.categoryLevel}>{scoreLevelLabel(cat.score)}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* 4 ── Vulnerabilities */}
        {vulnerableAreas.length > 0 && (
          <View style={S.section}>
            <Text style={S.sectionTitle}>Vulnérabilités identifiées</Text>
            {vulnerableAreas.map((area, i) => (
              <View
                key={i}
                style={[
                  S.vulnCard,
                  { borderLeftColor: riskColor(area.risk), backgroundColor: riskBg(area.risk) },
                ]}
              >
                <View style={S.vulnBody}>
                  <View style={S.vulnHeader}>
                    <View style={[S.vulnBadge, { backgroundColor: riskColor(area.risk) }]}>
                      <Text style={S.vulnBadgeText}>{riskLabel(area.risk)}</Text>
                    </View>
                    <Text style={S.vulnTitle}>{area.label}</Text>
                  </View>
                  <Text style={S.vulnDesc}>{area.description}</Text>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* 5 ── Recommendations */}
        <View style={S.section}>
          <Text style={S.sectionTitle}>Recommandations</Text>

          {useActionRecs ? (
            /* ── Improvement actions (top 6) */
            page1Actions.map((action) => (
              <View key={action.id} style={S.recRow}>
                <View style={[S.recBadge, { backgroundColor: priorityColor(action.priority) }]}>
                  <Text style={S.recBadgeText}>{priorityLabel(action.priority)}</Text>
                </View>
                <View style={S.recContent}>
                  <Text style={S.recTitle}>{action.action_title}</Text>
                  {action.description ? (
                    <Text style={S.recDesc} numberOfLines={2}>{action.description}</Text>
                  ) : null}
                </View>
                <Text style={S.recGain}>+{Number(action.score_impact)} pts</Text>
                <Text style={S.recCost}>{Number(action.estimated_cost).toLocaleString('fr-FR')} €</Text>
              </View>
            ))
          ) : (
            /* ── Category recommendations fallback */
            recommendations.map((rec, i) => (
              <View key={i} style={S.recRow}>
                <View style={[S.recBadge, { backgroundColor: priorityColor(rec.priority) }]}>
                  <Text style={S.recBadgeText}>{priorityLabel(rec.priority)}</Text>
                </View>
                <View style={S.recContent}>
                  <Text style={S.recTitle}>{rec.title}</Text>
                  {rec.items.slice(0, 2).map((item, j) => (
                    <Text key={j} style={S.recDesc}>→ {item}</Text>
                  ))}
                </View>
              </View>
            ))
          )}
        </View>

        {/* Footer */}
        <Footer page={1} />
      </Page>

      {/* ════════════════════════════════ PAGE 2 ════════════════════════════════ */}
      {hasPage2 && (
        <Page size="A4" style={S.page}>

          {/* 1 ── Simplified header */}
          <View style={S.headerSmall}>
            <Text style={S.headerSmallLeft}>{brandName}</Text>
            <Text style={S.headerSmallRight}>Plan d'amélioration</Text>
          </View>

          {/* 2 ── Summary banner */}
          <View style={S.summaryBanner}>
            <View style={S.summaryMetric}>
              <Text style={S.summaryMetricValue}>{activeActions.length}</Text>
              <Text style={S.summaryMetricLabel}>actions au total</Text>
            </View>
            <View style={S.summaryDivider} />
            <View style={S.summaryMetric}>
              <Text style={S.summaryMetricValue}>{highPriorityCount}</Text>
              <Text style={S.summaryMetricLabel}>actions prioritaires</Text>
            </View>
            <View style={S.summaryDivider} />
            <View style={S.summaryMetric}>
              <Text style={S.summaryMetricValue}>{totalCost.toLocaleString('fr-FR')} €</Text>
              <Text style={S.summaryMetricLabel}>Budget total</Text>
            </View>
          </View>

          {/* 3 ── Actions table */}
          <View style={S.tableContainer}>

            {/* Table header */}
            <View style={S.tableHeader}>
              <Text style={[S.tableHeaderText, { width: '55%' }]}>ACTION</Text>
              <Text style={[S.tableHeaderText, { width: '15%', textAlign: 'center' }]}>PRIORITE</Text>
              <Text style={[S.tableHeaderText, { width: '15%', textAlign: 'center' }]}>GAIN</Text>
              <Text style={[S.tableHeaderText, { width: '15%', textAlign: 'right' }]}>COUT ESTIME</Text>
            </View>

            {/* Rows */}
            {activeActions.map((action, index) => (
              <View key={action.id} wrap={false} style={index % 2 === 0 ? S.tableRow : S.tableRowAlt}>
                <View style={S.colAction}>
                  <Text style={S.actionTitle}>{action.action_title}</Text>
                  {action.description ? (
                    <Text style={S.actionDesc} numberOfLines={1}>{action.description}</Text>
                  ) : null}
                </View>
                <View style={S.colPriority}>
                  <View style={[S.tableBadge, { backgroundColor: priorityColor(action.priority) }]}>
                    <Text style={S.tableBadgeText}>{priorityLabel(action.priority)}</Text>
                  </View>
                </View>
                <View style={S.colGain}>
                  <Text style={S.tableGain}>+{Number(action.score_impact)} pts</Text>
                </View>
                <View style={S.colCost}>
                  <Text style={S.tableCost}>{Number(action.estimated_cost).toLocaleString('fr-FR')} €</Text>
                </View>
              </View>
            ))}
          </View>

          {/* Footer */}
          <Footer page={2} />
        </Page>
      )}

    </Document>
  );
}
